import {
  InventorySearchRequestSchema,
  InventorySearchResponseSchema,
  InventoryVehicleSchema,
  type InventorySearchRequest,
  type InventorySearchResponse,
  type InventoryVehicle,
} from "@lumika/shared";
import { apiFetch } from "./client";

/**
 * Public-shaped vehicle search client. Backs the dealer Inventory page
 * (read-only faceted grid) and any future external integrator surface.
 *
 * Path family: `/api/v1/vehicles/:dealershipId/...`. The dealership id
 * is taken from the authenticated session in the URL; the backend
 * cross-checks it against `auth.dealership.id` and rejects mismatches
 * with 403 — so a stolen URL can't poke at another tenant's inventory.
 */
export const vehiclesApi = {
  search: (
    dealershipId: string,
    body: InventorySearchRequest,
  ): Promise<InventorySearchResponse> => {
    // Validate locally so we surface bad client state as a thrown
    // error before the request goes out — the backend would reject it
    // anyway, but failing early gives the dev console better stacks.
    const validated = InventorySearchRequestSchema.parse(body);
    return apiFetch(
      `/vehicles/${dealershipId}/search`,
      InventorySearchResponseSchema,
      {
        method: "POST",
        body: JSON.stringify(validated),
      },
    );
  },

  getByVin: (dealershipId: string, vin: string): Promise<InventoryVehicle> =>
    apiFetch(
      `/vehicles/${dealershipId}/${encodeURIComponent(vin)}`,
      InventoryVehicleSchema,
    ),

  getById: (dealershipId: string, id: string): Promise<InventoryVehicle> =>
    apiFetch(
      `/vehicles/${dealershipId}/by-id/${encodeURIComponent(id)}`,
      InventoryVehicleSchema,
    ),

  /**
   * Returns the same-origin proxy URL for a vehicle photo by index.
   *
   * Loading the image through this URL (instead of the third-party CDN URL
   * stored in photos[]) prevents the canvas from being tainted by a
   * cross-origin image, so toBlob() succeeds when generating a poster.
   *
   * The backend validates that `index` is within the vehicle's own photos[]
   * before fetching — no SSRF, no cross-tenant access.
   */
  photoUrl: (dealershipId: string, vin: string, index = 0): string =>
    `/api/v1/vehicles/${dealershipId}/${encodeURIComponent(vin)}/photo?index=${index}`,
};
